import type { PreviousAnalysis, RoomEvent } from '@ai-meeting/shared'
import type { RealtimeToken } from '@soniox/client'
import { proxyEventToTokens, type ProxySttEvent } from '@/hooks/useProxySttRecording'
import {
  isSharedAssistCard,
  upsertAssistCard,
  type AssistCard,
} from '@/pages/Meetings/components/Copilot/assist.types'
import { applyMemoPane, isMemoPaneEvent } from '@/utils/applyMemoPane'

export interface RoomReplay {
  /**
   * Finalized lines, in the order the room said them, carrying one of the two
   * translation passes rather than both — see {@link withOneTranslationPass}.
   *
   * They keep the `start_ms` the room gave them. That is not this machine's
   * clock and was once stripped for exactly that reason, but the room has only
   * one clock: the server holds the origin and hands it to a late joiner on
   * `ready`, which `handleTimelineAnchor` adopts. Dropping it made every
   * recovered line save as if it had been spoken at the moment the room opened,
   * so the review screen stamped all of them `00:00`.
   */
  tokens: RealtimeToken[]
  /** The newest live summary the room produced, or null if it never did. */
  memo: PreviousAnalysis | null
  /**
   * The suggested Copilot questions the room produced, in their original order.
   *
   * None of them carries `elapsedMs`: the live path stamps that from the
   * reader's own meeting clock as each card lands, and a late joiner's clock is
   * not the one those cards happened on. The field is optional and the chip
   * renders without its time marker — the same bargain `withoutStartTime`
   * strikes for replayed transcript lines.
   */
  assistCards: AssistCard[]
}

/**
 * Turn a room's stored frames back into what the panes show (BRAI-539).
 *
 * Runs the frames through `proxyEventToTokens` — the same function the live
 * socket path uses — rather than reading the payloads here. The shapes belong
 * to a service that is not ours, and a second reader of them would drift from
 * the first the next time that service changed one.
 *
 * Partials are absent from the stored history by design, so only finals and
 * batches arrive, and one of the two translation passes is taken rather than
 * both — see {@link withOneTranslationPass}. The live-memo frames are the
 * other thing a late joiner has plainly missed, so they are folded back through
 * `applyMemoPane` — the same function the live socket uses, for the same reason
 * the tokens go back through `proxyEventToTokens`. A room that ran before the
 * memo was split into three panes replays from its stored `summarize.result`
 * rows and arrives at the same place. Assist cards fold back through
 * `upsertAssistCard`, again the function the live socket uses. Only
 * `query_suggestion` is public room data; answers and action results belong to
 * the participant who asked and are ignored even when older clients persisted
 * them before that privacy boundary was enforced. The rest —
 * `meeting-minutes.result`, `language.updated`, `translation.warning`,
 * `main-content.updated` — is stored and returned by the API but has no pane to
 * be replayed into yet; it is history, not a screen.
 *
 * @param events The room's frames, oldest first.
 */
export function roomEventsToReplay(events: readonly RoomEvent[]): RoomReplay {
  const finals: RealtimeToken[] = []
  const batch: RealtimeToken[] = []
  let memo: PreviousAnalysis | null = null
  let assistCards: AssistCard[] = []

  for (const event of events) {
    const frame = event.payload as ProxySttEvent
    if (!frame || typeof frame !== 'object') continue

    if (event.type === 'assist.card') {
      if (!isSharedAssistCard(frame)) continue
      // The frame is the card, exactly as the live path treats it — that path
      // spreads the whole event rather than picking fields out of it. Keeping
      // the two identical is what stops them drifting the next time the proxy
      // adds a field to a card.
      assistCards = upsertAssistCard(assistCards, frame as unknown as AssistCard)
      continue
    }

    if (isMemoPaneEvent(event.type)) {
      // Replayed in stored order, so the room's own sequence of merges and replaces is repeated
      // rather than guessed at. The stored `type` is what drives it, not `payload.event`: the type
      // is the column the server validated and deduplicated on, and the payload is whatever the
      // proxy happened to send.
      memo = applyMemoPane(memo, { event: event.type, data: frame.data })
      continue
    }

    const produced = proxyEventToTokens(frame)
    finals.push(...produced.finalTokens)
    // Held apart rather than appended beside them: the room translates every
    // turn twice and only one of the two passes belongs in the Output pane.
    batch.push(...produced.batchTokens)
  }

  return { tokens: withOneTranslationPass(finals, batch), memo, assistCards }
}

/**
 * The recovered lines that are not on screen already.
 *
 * A reader reads the room twice, and the second read overlaps its own socket: a
 * line said in the seconds around the socket opening can arrive down both
 * paths. The cursor cannot tell them apart, because the row was written after
 * the first read either way — so the two are told apart here, by what a line
 * is. The same frame relayed by whoever heard it yields the same speaker saying
 * the same words at the same moment on the room's clock, which `start_ms` now
 * carries; before it did, this could only have guessed from the text.
 *
 * @param recovered Lines from the second read.
 * @param shown     Lines already on screen, recovered and live alike.
 */
export function withoutTokensAlreadyShown(
  recovered: readonly RealtimeToken[],
  shown: readonly RealtimeToken[]
): RealtimeToken[] {
  const seen = new Set(shown.map(tokenIdentity))
  return recovered.filter((token) => !seen.has(tokenIdentity(token)))
}

function tokenIdentity(token: RealtimeToken): string {
  return [token.translation_status, token.speaker ?? '', token.start_ms ?? '', token.text].join('|')
}

/**
 * The room's lines carrying one translation pass instead of both.
 *
 * A room is translated twice over: `translation.final` as each sentence lands,
 * and `translation.batch` once the speaker finishes the turn. Live, only one of
 * them reaches the Output pane — `isBatchOnlyTranslationProvider` in
 * `MeetingChat` is the choice — while the batch pass is what Enhanced mode
 * renders. Replay took both, so a late joiner read every sentence twice in two
 * different wordings and the Output column ran to nearly twice the length of
 * the Input column beside it.
 *
 * Which pass wins is decided by what the room actually sent rather than by
 * provider name, the same way `prepareSave` decides it for the stored
 * translation. That covers azure and gpt-realtime-whisper, whose translation
 * only ever arrives in the batch pass, and covers equally a room whose inline
 * pass happened to produce nothing.
 *
 * The batch tokens go on the end rather than back where they were stored. Both
 * panes are filtered out of this single list by `translation_status`, so what
 * has to survive is the order of the translations among themselves, and that is
 * untouched.
 *
 * @param finals Source lines and the inline translation, in stored order.
 * @param batch  The per-turn pass, in stored order.
 */
function withOneTranslationPass(
  finals: readonly RealtimeToken[],
  batch: readonly RealtimeToken[]
): RealtimeToken[] {
  const translatedInline = finals.some((token) => token.translation_status === 'translation')
  return translatedInline ? [...finals] : [...finals, ...batch]
}

